import React, { useState, useEffect } from "react";

type SwipeSliderProps = {
  children: React.ReactNode[];
  currentIndex: number;
  onIndexChange: (newIndex: number) => void;
  className?: string;
};

const SwipeSlider: React.FC<SwipeSliderProps> = ({
  children,
  currentIndex,
  onIndexChange,
  className = "",
}) => {
  const [dragOffsetX, setDragOffsetX] = useState(0);
  const [touchStartX, setTouchStartX] = useState<number | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isTransitionEnabled, setIsTransitionEnabled] = useState(true);

  const SWIPE_THRESHOLD = 80;
  const width = window.innerWidth;

  const handleMouseMove = (e: MouseEvent) => {
    if (!isDragging || touchStartX === null) return;
    const diff = e.clientX - touchStartX;
    setDragOffsetX(diff);
  };

  const handleMouseUp = (e: MouseEvent) => {
    if (!isDragging || touchStartX === null) return;
    const diff = e.clientX - touchStartX;

    if (diff < -SWIPE_THRESHOLD && currentIndex < children.length - 1) {
      setDragOffsetX(-width);
      setTimeout(() => {
        onIndexChange(currentIndex + 1);
        setIsTransitionEnabled(false);
        setDragOffsetX(width);
        setTimeout(() => {
          setIsTransitionEnabled(true);
          setDragOffsetX(0);
        }, 20);
      }, 200);
    } else if (diff > SWIPE_THRESHOLD && currentIndex > 0) {
      setDragOffsetX(width);
      setTimeout(() => {
        onIndexChange(currentIndex - 1);
        setIsTransitionEnabled(false);
        setDragOffsetX(-width);
        setTimeout(() => {
          setIsTransitionEnabled(true);
          setDragOffsetX(0);
        }, 20);
      }, 200);
    } else {
      setDragOffsetX(0);
    }

    setTouchStartX(null);
    setIsDragging(false);
  };

  useEffect(() => {
    if (isDragging) {
      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleMouseUp);
    } else {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    }

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging, touchStartX, currentIndex, children.length]);

  const imageStyle = {
    transform: `translateX(${dragOffsetX}px)`,
    transition:
      isDragging || !isTransitionEnabled ? "none" : "transform 0.4s cubic-bezier(.4,0,.2,1)",
  };

  return (
    <div className={className} style={{ overflow: "hidden", position: "relative" }}>
      <div
        style={imageStyle}
        className="swipe-slider-container"
        onMouseDown={(e) => {
          setTouchStartX(e.clientX);
          setIsDragging(true);
        }}
        onTouchStart={(e) => {
          const touch = e.touches[0];
          setTouchStartX(touch.clientX);
          setIsDragging(true);
        }}
      >
        {children[currentIndex]}
      </div>
    </div>
  );
};

export default SwipeSlider;
